import {Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {get, useFormContext} from "react-hook-form";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckDouble, faHistory} from "@fortawesome/free-solid-svg-icons";
import {useParams} from "react-router-dom";

// Contexts
import {AuthContext} from "../../contexts/auth.js";
import {SettingsContext} from "../../contexts/settings.js";
import {useSocket} from "../../contexts/socket.js";
import {useToast} from "../../contexts/toast.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";
import useScrollListener from "../../hooks/useScrollListener.js";

// Utils
import {
  addCommas,
  exists,
  getCompletionRate,
  inViewport,
  prettyDateInUserTimezone
} from "../../utils/helpers.js";
import {
  buildFormulaString,
  evaluateRange,
  isElapsedStartAfterStop
} from "../../pages/checksheet-builder/helpers.js";
import {defaultUnacceptableParameterPrompt} from "../../utils/builder.js";

// Components
import InputError from "./InputError.js";
import InputTextArea from "./InputTextArea.js";
import InputSelect from "./InputSelect.js";
import Help from "../Help.js";
import FieldLinkHelp from "./FieldLinkHelp.js";

// Style
import {pad, radius} from "../../style/components/variables.js";
import {voice} from "../../style/components/typography.js";
import {
  Abbr,
  Button,
  CloseButton,
  Error,
  FormFieldWrapper,
  Inline,
  Input,
  Label,
  Pill,
  Small,
  Text
} from "../../style/components/general.js";

const PERCENT_COMPLETE = "percent_complete";
// Stored in place of a real value when a formula couldn't resolve; exists()
// treats these as truthy since they're non-empty strings, so callers that
// need to distinguish "has a real value" must check this list too.
const GENERATED_PLACEHOLDER_VALUES = ["_NO_PREVIOUS", "_DEP_IS_INF", "_CONDITION_UNSATISFIED"];

const InputGenerated = props => {
  const {
    testId = "input-generated",
    room,
    taskType = "checksheet",
    task,
    taskRecord,
    stage,
    name,
    label,
    tag,
    prompt,
    defaultValue,
    placeholder,
    required = true,
    readOnly = true,
    hidden = false,
    disabled = false,
    qualifier = false,
    absoluteValue = false,
    units = "",
    precision = 2,
    aMin,
    aMax,
    min,
    max,
    strictMin,
    strictMax,
    depends,
    trueDepends,
    formula,
    formulaString,
    condition,
    trueFormula,
    innerRef,
    globalUnacceptable,
    globalUnacceptableTitles,
    hasARange = false,
    hasSetPoint = false,
    setLow,
    setHigh,
    restrictedRangeException = "",
    rangeException = "",
    alertStartAfterStop = false,
    startAfterStopException = "",
    previousRead,
    errorDefault,
    weather,
    setFormulasEvaluating,
    shared = false,
    linkedAcrossStates = false,
    stateId
  } = props;

  const isMounted = useMountedState();

  const {slug} = useParams();
  const {addToast} = useToast();

  const {currentUser} = useContext(AuthContext);
  const {settings} = useContext(SettingsContext);
  const socket = useSocket();

  const scrolling = useScrollListener(document.getElementById("modal") || window);

  const {api} = useApi("formulas", {suppress: {error: true}});

  const generatedRef = useRef();
  const typing = useRef();
  /** Monotonic id per in-flight /formulas POST; stale responses must not setValue (out-of-order debounce). */
  const formulaEvalGenerationRef = useRef(0);

  const [inWindow, setInWindow] = useState(false);
  const [message, setMessage] = useState(null);
  const [formatted, setFormatted] = useState(null);
  const [backupFormulaString, setBackupFormulaString] = useState(null);
  const [hasNoBackup, setHasNoBackup] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const form = useFormContext();
  const {
    setValue,
    register,
    formState: {errors, submitCount},
    watch
  } = form;

  const value = watch(name);
  // A field needs to (re-)run its formula whenever its stored value isn't a
  // real computed result: unset, or a placeholder left by a formula that
  // couldn't previously resolve (previous-value lookup, infinite dependency,
  // or an unsatisfied visibility condition). Deriving this from the current
  // value each render - rather than a flag set once and cleared on the first
  // evaluation - means a placeholder written mid-session (e.g. by evaluate()
  // itself) still gets picked back up once its cause resolves.
  const needsEval = !exists(value) || GENERATED_PLACEHOLDER_VALUES.includes(value);
  const qualifierEnabled = watch(`${name}_qualifierEnabled`);
  const qualifierSelected = watch(`${name}_qualifier`);
  const locked = disabled || readOnly;

  const {byId} = useMemo(() => task?.builder || {}, [task]);

  const inMinutes = useMemo(() => byId?.[name]?.minutes || false, [byId, name]);

  const acceptableRangeDescription = useMemo(() => {
    if (exists(aMin) && !exists(aMax))
      return strictMin ? `(Must be > ${aMin})` : `(Must be ≥ ${aMin})`;
    if (!exists(aMin) && exists(aMax))
      return strictMax ? `(Must be < ${aMax})` : `(Must be ≤ ${aMax})`;
    return `(Must be between ${aMin} - ${aMax})`;
  }, [aMin, aMax, strictMin, strictMax]);

  const formValues = watch();
  const startAfterStopViolation = useMemo(
    () => isElapsedStartAfterStop({alertStartAfterStop, formula}, formValues),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alertStartAfterStop, formula, formValues]
  );

  const rangeViolation = hasARange && evaluateRange(value, props, qualifierSelected);
  const showUnacceptableComment = rangeViolation || startAfterStopViolation;
  const unacceptableCommentLabel = startAfterStopViolation
    ? startAfterStopException || defaultUnacceptableParameterPrompt
    : rangeException || defaultUnacceptableParameterPrompt;
  const unacceptableCommentDescription = startAfterStopViolation ? (
    <Error>Start time is after stop time</Error>
  ) : (
    <Error>{acceptableRangeDescription}</Error>
  );

  const hasYupErrors = useMemo(() => {
    const {message: yupMessage} = get(errors, name) || {};
    return !!yupMessage;
  }, [errors, name]);

  // follow dependencies of formula to find any potential default values
  const recurseFindDefaults = useCallback(
    (dep, fieldsWithDefault) => {
      const dependencies = dep ? dep.filter(d => d in byId).map(d => [d, byId[d]]) : [];

      for (let i = 0; i < dependencies.length; i++) {
        const [n, f] = dependencies[i];
        if (f && f.hasDivideByZeroDefault && exists(f.divideByZeroDefault))
          fieldsWithDefault.push(n);
        recurseFindDefaults(f.depends, fieldsWithDefault);
      }
    },
    [byId]
  );

  useEffect(() => {
    setFormatted(
      exists(value)
        ? addCommas(value, exists(precision) ? precision : 2, inMinutes ? 60 : null)
        : ""
    );
  }, [value, precision, inMinutes]);

  // watch linked fields dynamically
  const trueLinkedFields = {};
  trueDepends
    .filter(val => !!val)
    .forEach(d => {
      trueLinkedFields[d] = watch(d);
    });

  const linkedFields = {};
  depends
    .filter(val => !!val)
    .forEach(d => {
      linkedFields[d] = watch(d);
      linkedFields[`${d}_undefined`] = watch(`${d}_undefined`);
    });

  const latestVals = useRef({...trueLinkedFields, ...linkedFields});

  let changed = false;
  const temp = {...trueLinkedFields, ...linkedFields};

  const keys = Object.keys(temp);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (latestVals.current[key] !== undefined && temp[key] !== latestVals.current[key]) {
      changed = true;
      break;
    }
  }

  latestVals.current = {...temp};

  const updatePercentage = useCallback(() => {
    const {completedAt: _ca, note: _note, ...rest} = watch();
    if (isMounted() && room && socket && rest && taskType === "checksheet")
      socket.emit(
        PERCENT_COMPLETE,
        `${slug}-${room}`,
        `checksheet_${task.id}`,
        currentUser.publicId,
        getCompletionRate({...rest})
      );
  }, [task, taskType, watch, isMounted, room, socket, slug, currentUser]);

  useEffect(() => {
    if (!initializing) updatePercentage();
    setInitializing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (isMounted() && (hidden || (generatedRef.current && inViewport(generatedRef.current))))
      setInWindow(true);
  }, [isMounted, hidden]);

  useEffect(() => {
    if (isMounted() && scrolling && generatedRef.current) {
      const updated = hidden || inViewport(generatedRef.current);
      if (updated !== inWindow) setInWindow(updated);
    }
  }, [isMounted, scrolling, inWindow, hidden]);

  // New record context: cancel debounced eval and ignore any in-flight /formulas responses.
  useEffect(() => {
    if (typing.current) {
      clearTimeout(typing.current);
      typing.current = null;
    }
    formulaEvalGenerationRef.current += 1;
    if (setFormulasEvaluating) setFormulasEvaluating(prev => ({...prev, [name]: false}));
  }, [taskRecord?.id, name, setFormulasEvaluating]);

  const evaluate = useCallback(
    (fString, formulaObject) => {
      if (!readOnly) {
        const requestId = ++formulaEvalGenerationRef.current;
        if (setFormulasEvaluating) setFormulasEvaluating(prev => ({...prev, [name]: true}));
        api
          .callPost({
            [`${taskType}Id`]: task.id,
            [`${taskType}RecordId`]: taskRecord?.id,
            stage,
            stateId,
            formula: fString,
            ...formulaObject
          })
          .then(response => {
            if (requestId !== formulaEvalGenerationRef.current) return;

            const {status, data} = response;
            if (status === 200 && data) {
              // successful response
              setValue(name, data.computed, {shouldValidate: !!submitCount});
              setMessage(null);
              setValue(`${name}_undefined`, false);
            } else if (exists(errorDefault) && response?.includes("Division by zero")) {
              // division by zero, this field has a default
              setValue(name, Number.parseFloat(errorDefault), {shouldValidate: !!submitCount});
              setValue(`${name}_undefined`, true);
            } else if (response?.includes("Division by zero")) {
              // division by zero, no default, need to search dependencies for defaults
              if (!backupFormulaString && !hasNoBackup) {
                const fieldsWithDefault = [];
                recurseFindDefaults(depends, fieldsWithDefault);

                if (fieldsWithDefault.length) {
                  // if defaults found, build iterative formula
                  const str = buildFormulaString(formula, absoluteValue ?? false);
                  setBackupFormulaString(str);
                } else {
                  // will not try to find defaults again
                  setHasNoBackup(true);
                  setMessage(response);
                }
              } else setMessage(response);

              setValue(`${name}_undefined`, true, {shouldValidate: !!submitCount});
              setValue(name, null, {shouldValidate: !!submitCount});
            } else if (response?.includes("previous")) {
              setValue(name, "_NO_PREVIOUS", {shouldValidate: !!submitCount});
              setMessage(response);
            } else if (response?.includes("infinite")) {
              setValue(name, "_DEP_IS_INF", {shouldValidate: !!submitCount});
              setMessage(response);
            } else if (response !== "canceled") {
              addToast(response, "error");
            }
            if (response !== "canceled") updatePercentage();
          })
          .finally(() => {
            if (setFormulasEvaluating) {
              setFormulasEvaluating(prev => ({...prev, [name]: false}));
            }
          });
      }
    },
    [
      task,
      taskRecord,
      taskType,
      absoluteValue,
      addToast,
      api,
      backupFormulaString,
      depends,
      errorDefault,
      name,
      updatePercentage,
      setValue,
      submitCount,
      hasNoBackup,
      recurseFindDefaults,
      formula,
      readOnly,
      setFormulasEvaluating,
      stage,
      stateId
    ]
  );

  const handleInputRateLimit = useCallback(
    (fString, formulaObject) => {
      if (typing.current) {
        clearTimeout(typing.current);
      }
      // Mark that an evaluation is pending and update immediately
      // This ensures that subsequent same-value inputs will still be detected as changes
      const timer = setTimeout(() => {
        evaluate(fString, formulaObject);
        typing.current = null;
      }, 800);
      typing.current = timer;
    },
    [evaluate]
  );

  // normal, substituted evaluation
  useEffect(() => {
    const operands = {};
    let hasAllOperands = true;
    const hasPrevious = [];
    let sanitizedFormula = formulaString;
    const nameMap = {};
    let errorMessage = null;
    let fallbackVal = null;
    let prevSymbol = null;
    let prevOperand = null;
    let prevTimeVal = null;

    if (trueFormula && trueLinkedFields && !backupFormulaString) {
      for (let i = 0; i < trueFormula.length; i++) {
        const f = trueFormula[i];
        const fieldName = f.value;

        if (["rainfall", "cumulative", "precipitationChance"].includes(fieldName)) {
          const fieldValue = weather ? weather[fieldName] ?? 0 : 0;
          operands[fieldName] = `${fieldValue}`;
        } else if (fieldName in byId && !f.previous) {
          const fieldType = byId[fieldName]?.type;

          // formula part is a field that is not referencing a previous value
          const fieldValue = trueLinkedFields[fieldName];
          if (!exists(fieldValue)) {
            hasAllOperands = false;
            break;
          }
          if (fieldValue === "∞") {
            errorMessage = "Cannot evaluate! One or more operands are infinite.";
            fallbackVal = "_DEP_IS_INF";
            hasAllOperands = false;
            break;
          }

          // replace any characters that are invalid in a python variable name
          let sanitized = fieldName.replace(/[^A-Za-z0-9_]+/g, "_");
          if (sanitized.match(/^[0-9].*/)) {
            sanitized = `id_${sanitized}`;
          }

          // replace invalid names with sanitized version if necessary
          if (sanitized !== fieldName) {
            const re = new RegExp(fieldName, "g");
            sanitizedFormula = sanitizedFormula.replace(re, sanitized);
            nameMap[sanitized] = fieldName;
          }

          // add to operands array using sanitized name
          if (fieldType === "time") {
            let numericalTime = null;

            if (fieldValue?.includes(":")) {
              // convert field value from timestamp to numerical hours value
              const [hour, minute] = fieldValue.split(":");
              const hourParsed = parseInt(hour, 10);
              const minParsed = parseInt(minute, 10);

              numericalTime =
                !Number.isNaN(hourParsed) && !Number.isNaN(minParsed)
                  ? hourParsed + minParsed / 60
                  : null;

              if (
                prevSymbol === "-" &&
                exists(numericalTime) &&
                exists(prevTimeVal) &&
                prevTimeVal < numericalTime
              ) {
                // this is the manipulation necessary to yield the same result
                // as would be returned when considering the end time to be
                // falling on the following day
                // numericalTime -= 24;
                operands[prevOperand] = `${numericalTime}`;
                operands[sanitized] = `${prevTimeVal}`;
                prevTimeVal = null;
              } else {
                prevOperand = sanitized;
                prevTimeVal = numericalTime;
                operands[sanitized] = `${numericalTime}`;
              }
            }

            if (!exists(numericalTime)) {
              hasAllOperands = false;
              break;
            }
          } else operands[sanitized] = fieldValue;
        } else if (f.previous) {
          // formula part is a field that is referencing a previous value
          let sanitized = fieldName.replace(/[^A-Za-z0-9_]+/g, "_");
          if (sanitized.match(/^[0-9].*/)) {
            sanitized = `id_${sanitized}`;
          }

          // replace any characters that are invalid in a python variable name
          if (sanitized !== fieldName) {
            const re = new RegExp(fieldName, "g");
            sanitizedFormula = sanitizedFormula.replace(re, sanitized);
            nameMap[sanitized] = fieldName;
          }

          // add original name to previous value array for response lookup
          hasPrevious.push(sanitized);
        }

        prevSymbol = fieldName;
      }

      const shouldEvaluate = inWindow && hasAllOperands && (changed || needsEval);

      if (shouldEvaluate) {
        handleInputRateLimit(sanitizedFormula, {operands, hasPrevious, name, nameMap});
      } else if (!hasAllOperands) {
        setValue(name, fallbackVal, {shouldValidate: !!submitCount});
        setMessage(errorMessage);
        clearTimeout(typing.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...Object.keys(trueLinkedFields).map(f => trueLinkedFields[f]),
    trueFormula,
    inWindow,
    taskRecord?.id
  ]);

  // backup, iterative evaluation
  // only fires when a division by zero has occurred
  useEffect(() => {
    const operands = {};
    let hasAllOperands = true;
    const hasPrevious = [];
    let sanitizedFormula = backupFormulaString;
    const nameMap = {};
    let errorMessage = null;
    let fallbackVal = null;
    let prevSymbol = null;
    let prevTimeVal = null;

    if (formula && linkedFields && backupFormulaString) {
      for (let i = 0; i < formula.length; i++) {
        const f = formula[i];
        const fieldName = f.value;
        if (["rainfall", "cumulative", "precipitationChance"].includes(fieldName)) {
          const fieldValue = weather ? weather[fieldName] ?? 0 : 0;
          operands[fieldName] = `${fieldValue}`;
        } else if (fieldName in byId && !f.previous) {
          const fieldType = byId[fieldName]?.type;

          const {hasDivideByZeroDefault: fieldHasDefault, divideByZeroDefault: fieldDefault} =
            byId[fieldName];
          let fieldValue = linkedFields[fieldName];
          const undef = linkedFields[`${fieldName}_undefined`];
          // if the field in question is undefined (divided by zero) and has a default, replace
          if (undef && !fieldValue) fieldValue = fieldHasDefault ? `${fieldDefault}` : "";

          if (!exists(fieldValue)) {
            hasAllOperands = false;
            break;
          }

          if (fieldValue === "∞") {
            errorMessage = "Cannot evaluate! One or more operands are infinite.";
            fallbackVal = "_DEP_IS_INF";
            hasAllOperands = false;
            break;
          }

          // replace any characters that are invalid in a python variable name
          let sanitized = fieldName.replace(/[^A-Za-z0-9_]+/g, "_");
          if (sanitized.match(/^[0-9].*/)) {
            sanitized = `id_${sanitized}`;
          }

          // replace invalid names with sanitized version if necessary
          if (sanitized !== fieldName) {
            const re = new RegExp(fieldName, "g");
            sanitizedFormula = sanitizedFormula.replace(re, sanitized);
            nameMap[sanitized] = fieldName;
          }

          if (fieldType === "time") {
            let numericalTime = null;
            if (fieldValue?.includes(":")) {
              // convert field value from timestamp to numerical hours value
              const [hour, minute] = fieldValue.split(":");
              const hourParsed = parseInt(hour, 10);
              const minParsed = parseInt(minute, 10);

              numericalTime =
                !Number.isNaN(hourParsed) && !Number.isNaN(minParsed)
                  ? hourParsed + minParsed / 60
                  : null;

              if (
                prevSymbol === "-" &&
                exists(numericalTime) &&
                exists(prevTimeVal) &&
                prevTimeVal < numericalTime
              ) {
                // this is the manipulation necessary to yield the same result
                // as would be returned when considering the end time to be
                // falling on the following day
                numericalTime -= 24;
                prevTimeVal = null;
              } else {
                prevTimeVal = numericalTime;
              }
            }

            if (exists(numericalTime)) operands[sanitized] = `${numericalTime}`;
            else {
              hasAllOperands = false;
              break;
            }
          } else operands[sanitized] = fieldValue;
        } else if (f.previous) {
          // formula part is a field that is referencing a previous value
          let sanitized = fieldName.replace(/[^A-Za-z0-9_]+/g, "_");
          if (sanitized.match(/^[0-9].*/)) {
            sanitized = `id_${sanitized}`;
          }

          // replace any characters that are invalid in a python variable name
          if (sanitized !== fieldName) {
            const re = new RegExp(fieldName, "g");
            sanitizedFormula = sanitizedFormula.replace(re, sanitized);
            nameMap[sanitized] = fieldName;
          }

          hasPrevious.push(sanitized);
        }

        prevSymbol = fieldName;
      }

      // Allow evaluation if changed OR if this field still needs an eval (initial load or placeholder)
      const shouldEvaluate = inWindow && hasAllOperands && (changed || needsEval);

      if (shouldEvaluate) {
        handleInputRateLimit(sanitizedFormula, {operands, hasPrevious, name, nameMap});
      } else if (!hasAllOperands) {
        setValue(name, fallbackVal, {shouldValidate: !!submitCount});
        setMessage(errorMessage);
        clearTimeout(typing.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...Object.keys(linkedFields).map(f => linkedFields[f]),
    formula,
    backupFormulaString,
    inWindow,
    taskRecord?.id
  ]);

  const readableFormula = useMemo(
    () => (
      <>
        {formula.map((part, idx) => {
          const space = idx < formula.length - 1 ? <>&nbsp;</> : null;
          if (byId && part.value in byId)
            return part.previous ? (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={idx}>
                {byId[part.value].label} <InlineIcon icon={faHistory} />
                {space}
              </Fragment>
            ) : (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={idx}>
                {byId[part.value].label}
                {space}
              </Fragment>
            );
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={idx}>
              {part.label}
              {space}
            </Fragment>
          );
        })}
      </>
    ),
    [formula, byId]
  );

  const readableFormulaString = useMemo(() => {
    let f = "";
    formula.map(part => {
      if (byId && part.value in byId)
        f += ` ${byId[part.value].label} ${part?.previous ? " PREVIOUS" : ""}`;
      else f += ` ${part.label}`;
    });
    return f;
  }, [byId, formula]);

  const refUnits = useRef();

  return (
    <>
      <FormFieldWrapper ref={generatedRef} data-testid={testId}>
        {label && (
          <FieldLabel bold inline onClick={e => e.preventDefault()}>
            {globalUnacceptable}
            {label.toUpperCase()}
            {required && "*"}
            {tag && (
              <Pill quiet>
                <Abbr title={tag}>{tag}</Abbr>
              </Pill>
            )}
            <FieldLinkHelp shared={shared} linkedAcrossStates={linkedAcrossStates} />
            <Help title={readableFormulaString} icon={<Formula>f(x)</Formula>}>
              {readableFormula}
            </Help>
            {condition && <Help icon={<FontAwesomeIcon icon={faCheckDouble} />}>{condition}</Help>}
            {previousRead && exists(previousRead.value) && (
              <Help icon={<FontAwesomeIcon icon={faHistory} />}>
                {previousRead?.value !== "_NO_PREVIOUS" ? (
                  <>
                    Previous&nbsp;Read:&nbsp;
                    {addCommas(
                      previousRead.value,
                      exists(precision) ? precision : 2,
                      inMinutes ? 60 : null
                    )}
                    &nbsp;
                    {inMinutes ? "minutes" : units}
                    &nbsp;on&nbsp;
                    {prettyDateInUserTimezone(previousRead.date, settings.timezone, "MMM D YYYY")}
                  </>
                ) : (
                  "No previous record, data will be available on next submission."
                )}
              </Help>
            )}
          </FieldLabel>
        )}

        {prompt && <Prompt>{prompt}</Prompt>}

        <Inline>
          {qualifier && !qualifierEnabled && (
            <StyledButton
              type="button"
              disabled={locked}
              onClick={() => {
                if (locked) return;
                setValue(`${name}_qualifierEnabled`, true, {shouldValidate: !!submitCount});
              }}>
              Qualifier
            </StyledButton>
          )}

          {qualifier && qualifierEnabled && (
            <>
              <StyledCloseButton
                data-testid="close-button"
                type="button"
                disabled={locked}
                onClick={() => {
                  if (locked) return;
                  setValue(`${name}_qualifier`, null, {shouldValidate: !!submitCount});
                  setValue(`${name}_qualifierEnabled`, false, {shouldValidate: !!submitCount});
                }}
              />
              <SelectWrapper>
                <InputSelect
                  name={`${name}_qualifier`}
                  placeholder="Select..."
                  options={["<", ">", "≤", "≥", "ND", "DNQ", "EST"]}
                  disabled={locked}
                />
              </SelectWrapper>
            </>
          )}

          <InputWrapper>
            {units && (
              <Units data-testid={`${testId}-units`} ref={refUnits}>
                {inMinutes ? "minutes" : units}
              </Units>
            )}
            <NumberInput
              id={name}
              ref={innerRef}
              type="number"
              data-testid={`${testId}-input`}
              placeholder={placeholder}
              defaultValue={defaultValue}
              disabled={disabled}
              step="any"
              size="10"
              hidden
              {...register(name, {required})}
            />
            <NumberInput
              type="text"
              placeholder={placeholder}
              disabled={locked}
              unitPad={refUnits.current?.clientWidth}
              defaultValue={!GENERATED_PLACEHOLDER_VALUES.includes(value) ? formatted : null}
            />
          </InputWrapper>
          <Info>{hasSetPoint && `(Set Point: ${setLow} - ${setHigh})`}</Info>
        </Inline>
        {message && (
          <Notice error={message && message.includes("Cannot evaluate!") ? 1 : 0}>{message}</Notice>
        )}
        <InputError errors={errors} name={name} />
        {!message && !hasYupErrors && globalUnacceptableTitles?.length && (
          <Error>
            Parameter violation{globalUnacceptableTitles.length > 1 ? "s" : ""}:{" "}
            {globalUnacceptableTitles.join(", ")}
          </Error>
        )}
      </FormFieldWrapper>
      {showUnacceptableComment && (
        <InputTextArea
          name={`${name}_comment`}
          label={unacceptableCommentLabel}
          description={unacceptableCommentDescription}
          required
          maxLength={1000}
          disabled={readOnly}
        />
      )}
      {restrictedRangeException &&
        exists(value) &&
        (exists(min) || exists(max)) &&
        submitCount === 0 &&
        evaluateRange(value, props, qualifierSelected) && <Error>{restrictedRangeException}</Error>}
    </>
  );
};

InputGenerated.propTypes = {
  room: PropTypes.string,
  testId: PropTypes.string,
  task: PropTypes.objectOf(PropTypes.any).isRequired,
  taskRecord: PropTypes.objectOf(PropTypes.any),
  taskType: PropTypes.string,
  stage: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  tag: PropTypes.string,
  prompt: PropTypes.string,
  precision: PropTypes.number,
  defaultValue: PropTypes.number,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  hidden: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  units: PropTypes.string,
  depends: PropTypes.arrayOf(PropTypes.string).isRequired,
  trueDepends: PropTypes.arrayOf(PropTypes.string).isRequired,
  formula: PropTypes.arrayOf(PropTypes.any).isRequired,
  trueFormula: PropTypes.arrayOf(PropTypes.any).isRequired,
  formulaString: PropTypes.string.isRequired,
  condition: PropTypes.string,
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({current: PropTypes.instanceOf(Element)})
  ]),
  maxWidth: PropTypes.bool,
  globalUnacceptable: PropTypes.node,
  globalUnacceptableTitles: PropTypes.arrayOf(PropTypes.string),
  aMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  aMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  strictMin: PropTypes.bool,
  strictMax: PropTypes.bool,
  hasARange: PropTypes.bool,
  rangeException: PropTypes.string,
  alertStartAfterStop: PropTypes.bool,
  startAfterStopException: PropTypes.string,
  restrictedRangeException: PropTypes.string,
  hasSetPoint: PropTypes.bool,
  setLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  previousRead: PropTypes.objectOf(PropTypes.any),
  qualifier: PropTypes.bool,
  errorDefault: PropTypes.string,
  absoluteValue: PropTypes.bool,
  weather: PropTypes.objectOf(PropTypes.any),
  setFormulasEvaluating: PropTypes.func,
  shared: PropTypes.bool,
  linkedAcrossStates: PropTypes.bool,
  stateId: PropTypes.number
};

// Style Overrides
const FieldLabel = styled(Label)`
  white-space: normal;
  line-height: normal;
  gap: ${pad / 2}px;
  margin-bottom: 2px;
`;

const Prompt = styled(Text)`
  margin-top: 2px;
  margin-bottom: ${pad / 2}px;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Units = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  padding: ${pad / 2}px;
  line-height: initial;
  color: ${({theme}) => theme.tertiary};
  background: ${({theme}) => theme.secondary};
  border-top-right-radius: ${radius};
  border-bottom-right-radius: ${radius};
`;

const NumberInput = styled(Input)`
  &[type="text"],
  &[type="number"] {
    width: ${({unitPad}) => (unitPad ? `${150 + unitPad / 2}px` : "150px")};
    max-width: 200px;
  }

  ${({unitPad}) =>
    unitPad &&
    css`
      padding-right: ${unitPad}px;
    `}
`;

const Notice = styled(Small)`
  padding: ${pad}px 0;

  ${({error}) =>
    error &&
    css`
      color: ${error};
    `}
`;

const Info = styled.span`
  color: ${({theme}) => theme.secondary};
  margin-left: ${pad}px;
`;

const SelectWrapper = styled.div`
  width: 100px;
  margin-right: ${pad}px;
  margin-top: -${pad}px;
`;

const StyledCloseButton = styled(CloseButton)`
  margin-right: ${pad}px;
  margin-left: 0;
`;

const StyledButton = styled(Button)`
  margin-right: ${pad}px;
  padding: ${pad}px;
  ${voice.quiet}
`;

const InlineIcon = styled(FontAwesomeIcon)`
  fill: ${({theme}) => theme.tertiary};
`;

const Formula = styled.span`
  ${voice.quiet};
  font-weight: bold;
  color: ${({theme}) => theme.secondary};
`;

export default InputGenerated;
